// ---------------------------------------------------------------------------
// Theme registry with base inheritance (Phase 2 §3.2, §4, §6).
//
// FRONTEND-ONLY, BUILD-TIME AUTHORING CONSTRUCT. A `ThemeDefinition` expresses a
// theme as *deviations* from a base; `resolveThemeDefinition` flattens it onto the
// base to produce a complete `ResolvedTheme`. That resolved snapshot is what gets
// embedded on a deck (`presentation.theme`) and is all any consumer ever sees —
// renderer, checker, text-fit, backend, Deno exporter. The registry itself is
// never stored and is NOT duplicated into vercel-backend or the Deno exporter
// (§4.1): the model never selects a theme, the picker runs in the frontend, and
// the exporter resolves the already-embedded snapshot.
// ---------------------------------------------------------------------------

import type {
  ColorToken,
  DecorationElement,
  ElementPosition,
  ElementPositionOverride,
  FontToken,
  LayoutDefinition,
  PlaceholderDefinition,
  PlaceholderId,
  ResolvedTheme,
  Slide,
  SlideContentV1,
  SlideTemplate,
  SlideTextStyle,
} from '../types/slide';

import { DEFAULT_THEME } from './default-theme';
import { EDITORIAL_THEME } from './themes/editorial';
import { LA_FOLLETTE_BANNER_THEME } from './themes/la-follette-banner';
import { LA_FOLLETTE_THEME } from './themes/la-follette';
import { PROFESSIONAL_THEME } from './themes/professional';

// -- Authoring types (NEW; NOT in slide.ts — these are not stored content) -----

export type ThemeId = 'default' | 'professional' | 'editorial' | 'laFollette' | 'laFolletteBanner';

/** A per-placeholder override (§4.2). Any field may be omitted to inherit it from
 *  the base placeholder of the same `placeholderId`: `position` is a partial
 *  override merged per-coordinate; `textStyle` is merged per-field. */
export interface PlaceholderOverride {
  placeholderId: PlaceholderId;
  position?: ElementPositionOverride;
  textStyle?: SlideTextStyle;
}

/** A partial LayoutDefinition: any of background / placeholders / decoration may
 *  be omitted to inherit that whole field from the base theme's layout (§3.2). When
 *  `placeholders` IS present, each entry is merged onto the base placeholder of the
 *  same `placeholderId` (§4.2) — not a whole-array replace. */
export interface LayoutOverride {
  background?: string;
  placeholders?: PlaceholderOverride[];
  decoration?: DecorationElement[];
}

/** The DRY authoring form. Only deviations from `baseThemeId` are listed; the
 *  resolver flattens them onto the base to produce a full `ResolvedTheme` (§3.2). */
export interface ThemeDefinition {
  themeId: ThemeId;
  name: string;
  /** Absent ⇒ this is a base theme (its partials are treated as complete). */
  baseThemeId?: ThemeId;
  palette?: Partial<Record<ColorToken, string>>;
  fonts?: Partial<Record<FontToken, string>>;
  /** Per-template overrides; an absent template inherits the base layout whole. */
  layouts?: Partial<Record<SlideTemplate, LayoutOverride>>;
}

const SLIDE_TEMPLATES: SlideTemplate[] = [
  'BLANK',
  'TITLE',
  'TITLE_AND_BODY',
  'TITLE_AND_TWO_COLUMNS',
];

// Resolved bases available to the flattener. `default` is `DEFAULT_THEME` itself —
// seeding this map (rather than reading the half-built `THEME_REGISTRY`) avoids a
// module init-order / TDZ hazard. Only `default` is ever a base in Phase 2.
const RESOLVED_BASES: Partial<Record<ThemeId, ResolvedTheme>> = { default: DEFAULT_THEME };

// -- Resolution / flattening (§4.2) -------------------------------------------

/** Editor hard-default geometry, used to complete a brand-new (base-less)
 *  placeholder whose override omits coordinates. Mirrors the renderer's default. */
const HARD_DEFAULT_POSITION: ElementPosition = {
  x: 80,
  y: 80,
  width: 240,
  height: 80,
  rotation: 0,
  zIndex: 1,
};

/** Partial position override merged per-coordinate over a base (full) position. */
function mergePlaceholderPosition(
  override: ElementPositionOverride | undefined,
  base: ElementPosition | undefined
): ElementPosition {
  const b = base ?? HARD_DEFAULT_POSITION;
  return {
    x: override?.x ?? b.x,
    y: override?.y ?? b.y,
    width: override?.width ?? b.width,
    height: override?.height ?? b.height,
    rotation: override?.rotation ?? b.rotation,
    zIndex: override?.zIndex ?? b.zIndex,
  };
}

/** Merge one placeholder override onto its (possibly absent) base placeholder:
 *  position per-coordinate, textStyle per-field (override wins; omitted inherits). */
function mergePlaceholder(
  base: PlaceholderDefinition | undefined,
  override: PlaceholderOverride
): PlaceholderDefinition {
  const result: PlaceholderDefinition = {
    placeholderId: override.placeholderId,
    position: mergePlaceholderPosition(override.position, base?.position),
  };
  const textStyle: SlideTextStyle = { ...(base?.textStyle ?? {}), ...(override.textStyle ?? {}) };
  if (Object.keys(textStyle).length > 0) result.textStyle = textStyle;
  return result;
}

/** Merge override placeholders onto base placeholders BY `placeholderId` (§4.2):
 *  matched → per-field merge; base-only → kept; override-only → appended. Order is
 *  base order, then appended override-only placeholders in override order. There is
 *  no remove operator. */
function mergePlaceholders(
  base: PlaceholderDefinition[],
  overrides: PlaceholderOverride[]
): PlaceholderDefinition[] {
  const overrideById = new Map(overrides.map((o) => [o.placeholderId, o]));
  const matched = new Set<PlaceholderId>();
  const merged = base.map((basePlaceholder) => {
    const override = overrideById.get(basePlaceholder.placeholderId);
    if (!override) return basePlaceholder;
    matched.add(basePlaceholder.placeholderId);
    return mergePlaceholder(basePlaceholder, override);
  });
  for (const override of overrides) {
    if (!matched.has(override.placeholderId)) merged.push(mergePlaceholder(undefined, override));
  }
  return merged;
}

/** Flatten a `ThemeDefinition` onto its base into a complete `ResolvedTheme`.
 *  Palette/fonts shallow-merge over the base; per template, background and
 *  decoration inherit whole unless overridden, while placeholders merge BY
 *  `placeholderId` per-field (§4.2) — so a layout can change one placeholder field
 *  without restating the rest, or add decoration while keeping the base geometry. */
export function resolveThemeDefinition(def: ThemeDefinition): ResolvedTheme {
  const base = def.baseThemeId ? RESOLVED_BASES[def.baseThemeId] : undefined;
  if (def.baseThemeId && !base) {
    throw new Error(`Theme "${def.themeId}" references unknown base "${def.baseThemeId}"`);
  }

  const palette = { ...(base?.palette ?? {}), ...(def.palette ?? {}) } as Record<
    ColorToken,
    string
  >;
  const fonts = { ...(base?.fonts ?? {}), ...(def.fonts ?? {}) } as Record<FontToken, string>;

  const layouts = {} as Record<SlideTemplate, LayoutDefinition>;
  for (const template of SLIDE_TEMPLATES) {
    const baseLayout = base?.layouts[template];
    const override = def.layouts?.[template];
    const basePlaceholders = baseLayout?.placeholders ?? [];
    layouts[template] = {
      background: override?.background ?? baseLayout?.background ?? '$background',
      placeholders: override?.placeholders
        ? mergePlaceholders(basePlaceholders, override.placeholders)
        : basePlaceholders,
      decoration: override?.decoration ?? baseLayout?.decoration ?? [],
    };
  }

  return { themeId: def.themeId, name: def.name, palette, fonts, layouts };
}

// -- Registry (built once at module load, §4.2) -------------------------------

/** All built-in themes, keyed by `ThemeId`, each a complete `ResolvedTheme`. The
 *  `default` entry is `DEFAULT_THEME` (it doubles as the inheritance base). */
export const THEME_REGISTRY: Record<ThemeId, ResolvedTheme> = {
  default: DEFAULT_THEME,
  professional: resolveThemeDefinition(PROFESSIONAL_THEME),
  editorial: resolveThemeDefinition(EDITORIAL_THEME),
  laFollette: resolveThemeDefinition(LA_FOLLETTE_THEME),
  laFolletteBanner: resolveThemeDefinition(LA_FOLLETTE_BANNER_THEME),
};

/** Drives the picker (§7). Order = display order. */
export const THEME_LIST: { themeId: ThemeId; name: string }[] = [
  { themeId: 'default', name: THEME_REGISTRY.default.name },
  { themeId: 'professional', name: THEME_REGISTRY.professional.name },
  { themeId: 'editorial', name: THEME_REGISTRY.editorial.name },
  { themeId: 'laFollette', name: THEME_REGISTRY.laFollette.name },
  { themeId: 'laFolletteBanner', name: THEME_REGISTRY.laFolletteBanner.name },
];

// -- Apply (§6) ---------------------------------------------------------------

/** Fresh, owned clone of a registry snapshot — the single place apply / re-apply
 *  reads from, so the editor and the pure op below can never diverge. Re-apply
 *  picks up registry drift because this re-reads the current constant (§6.2). */
export function registryThemeSnapshot(themeId: ThemeId): ResolvedTheme {
  return structuredClone(THEME_REGISTRY[themeId]);
}

/** Deep structural equality for plain JSON values (themes are JSON-safe: no
 *  functions/dates/regex). Order-independent across object keys. */
export function themesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  const aArr = Array.isArray(a);
  const bArr = Array.isArray(b);
  if (aArr !== bArr) return false;
  if (aArr && bArr) {
    if (a.length !== b.length) return false;
    return a.every((item, i) => themesEqual(item, b[i]));
  }
  const aObj = a as Record<string, unknown>;
  const bObj = b as Record<string, unknown>;
  const aKeys = Object.keys(aObj);
  const bKeys = Object.keys(bObj);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every(
    (key) => Object.prototype.hasOwnProperty.call(bObj, key) && themesEqual(aObj[key], bObj[key])
  );
}

/**
 * Pure theme apply (§6.1). Re-embeds `themeId`'s freshly-resolved snapshot onto a
 * deck, preserving every slide's content + overrides as-is (Decision 8a — no
 * text-fit, no `fontSize` stripping). Decoration is render-only and regenerated at
 * render time, so nothing is written into `slide.elements`. `schemaVersion` stays
 * `'1'`; the only changed field is `presentation.theme`.
 *
 * D1 (skip-when-identical): returns the SAME content reference when the resolved
 * snapshot deep-equals the embedded theme, so callers can detect "no change" and
 * skip pushing history / marking dirty. Registry drift still propagates — a
 * drifted snapshot differs and is re-embedded.
 */
export function applyThemeToContent(content: SlideContentV1, themeId: ThemeId): SlideContentV1 {
  const next = registryThemeSnapshot(themeId);
  if (themesEqual(content.presentation.theme, next)) return content;
  return { ...content, presentation: { ...content.presentation, theme: next } };
}

// -- Picker preview fixture (§7.2) --------------------------------------------

/** Canonical sample slide for theme previews — a `TITLE_AND_BODY` so palette,
 *  fonts, and decoration (top bar + title underline) are all visible. */
export const SAMPLE_SLIDE: Slide = {
  id: 'theme-sample-slide',
  template: 'TITLE_AND_BODY',
  elements: [
    {
      id: 'theme-sample-title',
      type: 'text',
      placeholderId: 'TITLE',
      content: { text: 'Sample title' },
    },
    {
      id: 'theme-sample-body',
      type: 'text',
      placeholderId: 'BODY',
      content: { text: 'Body text shows the fonts and colors.\nDecoration renders automatically.' },
    },
  ],
};
